import { useState, useEffect } from "react";

const blobToDataUrl = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Failed to read blob as data URL"));
    reader.readAsDataURL(blob);
  });
};

const fetchToDataUrl = async (src: string): Promise<string> => {
  const res = await fetch(src, { cache: "force-cache" });
  if (!res.ok) throw new Error(`Failed to fetch image: ${res.status}`);
  const blob = await res.blob();
  return await blobToDataUrl(blob);
};

/**
 * Converts an image URL to a data URL (base64).
 * This is useful for html-to-image which has trouble with external/imported images.
 */
export const useImageDataUrl = (imageSrc: string): string | null => {
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    const loadImage = async () => {
      try {
        const img = new Image();
        img.crossOrigin = "anonymous";
        
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = reject;
          img.src = imageSrc;
        });

        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          const result = canvas.toDataURL("image/png");
          setDataUrl(result);
        }
      } catch (error) {
        console.error("Failed to convert image to data URL:", error);
        // Fallback to original src
        setDataUrl(imageSrc);
      }
    };

    loadImage();
  }, [imageSrc]);

  return dataUrl;
};

/**
 * Synchronously preload and convert image to data URL.
 * Returns a promise that resolves to the data URL.
 */
export const imageToDataUrl = async (imageSrc: string): Promise<string> => {
  // Prefer fetch+FileReader (no canvas tainting/CORS surprises on iOS webviews)
  try {
    const dataUrl = await fetchToDataUrl(imageSrc);
    if (dataUrl.startsWith("data:image/")) return dataUrl;
  } catch (error) {
    console.warn("Failed to fetch image for dataURL:", error);
  }

  // Fallback to the previous Image+Canvas approach
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;

        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        } else {
          resolve(imageSrc);
        }
      } catch (error) {
        console.error("Canvas error:", error);
        resolve(imageSrc);
      }
    };

    img.onerror = () => {
      console.error("Failed to load image:", imageSrc);
      resolve(imageSrc);
    };

    img.src = imageSrc;
  });
};
